// Iter-193 — Freelance invoices payments due dashboard.
// Lists all VALIDATED (INVOICE_PROCESSING) freelance invoices that aren't paid yet,
// sorted by upcoming/overdue payment due date. Surfaces totals, alert badges and the
// configured overdue thresholds.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, Banknote, Check, Clock, Loader2, Pencil, RefreshCw, X } from "lucide-react";

const fmtAmt = (n, ccy) =>
  n === null || n === undefined ? "—" : `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${ccy || ""}`;

const dueTone = (level, d) => {
  if (level === 2) return "bg-red-100 text-red-900 border-red-300";
  if (level === 1) return "bg-amber-100 text-amber-900 border-amber-300";
  if (d == null) return "bg-zinc-100 text-zinc-700 border-zinc-200";
  if (d < 0) return "bg-orange-50 text-orange-800 border-orange-200";
  if (d <= 7) return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-emerald-50 text-emerald-800 border-emerald-200";
};

export default function PaymentsDue() {
  const { user } = useAuth();
  const canEdit = user && (user.role === "CEO" || user.role === "GESTIONNAIRE");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/freelance/admin/payments-due");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="px-6 py-12 flex items-center gap-2 text-zinc-500"><Loader2 size={16} className="animate-spin" /> Chargement…</div>;
  }
  if (!data) return null;

  const items = data.items || [];
  const totals = Object.entries(data.totals_ttc_by_currency || {});
  return (
    <div className="px-6 py-8 max-w-7xl mx-auto" data-testid="payments-due-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Paiements à régler</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Factures freelance validées en attente de règlement, triées par échéance. Alertes après {data.thresholds.alert1_days} j / {data.thresholds.alert2_days} j de retard.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="payments-due-refresh"
          className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-zinc-200 text-zinc-700 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Kpi label="Factures" value={data.summary.count} testid="kpi-count" icon={Banknote} />
        <Kpi label="Alerte n°1" value={data.summary.alert1_count} tone="text-amber-700" testid="kpi-alert1" hint={`≥ ${data.thresholds.alert1_days} j de retard`} icon={Clock} />
        <Kpi label="Alerte n°2" value={data.summary.alert2_count} tone="text-red-700" testid="kpi-alert2" hint={`≥ ${data.thresholds.alert2_days} j de retard`} icon={AlertTriangle} />
        <Kpi
          label="Total TTC dû"
          value={totals.length === 0 ? "—" : totals.map(([c, v]) => `${Number(v).toLocaleString("fr-FR")} ${c}`).join(" / ")}
          testid="kpi-total"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                <th className="px-4 py-2">Freelance / mois</th>
                <th className="px-4 py-2">Facture</th>
                <th className="px-4 py-2 text-right">Montant TTC</th>
                <th className="px-4 py-2 text-right">Délai</th>
                <th className="px-4 py-2 text-right">Échéance</th>
                <th className="px-4 py-2 text-right">État</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-zinc-500">Aucune facture en attente de règlement.</td></tr>
              )}
              {items.map((r) => {
                const d = r.days_until_due;
                const ovd = r.days_overdue;
                const lvl = r.alert_level;
                const stateLabel = ovd != null ? `Retard ${ovd} j` : d != null ? `J-${d}` : "—";
                return (
                  <tr key={r.freelance_invoice_id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`payments-due-row-${r.freelance_invoice_id}`}>
                    <td className="px-4 py-3">
                      <Link to="/freelance/admin" className="font-medium text-zinc-900 hover:underline">{r.user_name || r.user_email}</Link>
                      <div className="text-[10px] text-zinc-500 font-mono">{r.label || `${r.year}-${String(r.month).padStart(2, "0")}`}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {r.invoice_number || r.freelance_invoice_id}
                      {r.submitted_at && (
                        <div className="text-[10px] text-zinc-400">Soumis le {new Date(r.submitted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{fmtAmt(r.amount_ttc, r.currency)}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-zinc-600">
                      <DelayEditor
                        invoiceId={r.freelance_invoice_id}
                        value={r.payment_delay_days}
                        canEdit={canEdit}
                        onSaved={(resp) => {
                          // Iter-193c — patch the row in place rather than reloading the whole table
                          // (the row position is preserved which preserves user's scroll).
                          setData((d) => d ? {
                            ...d,
                            items: d.items.map((it) => it.freelance_invoice_id === r.freelance_invoice_id
                              ? { ...it, payment_delay_days: resp.payment_delay_days, payment_due_date: resp.payment_due_date }
                              : it),
                          } : d);
                        }}
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.payment_due_date ? new Date(r.payment_due_date + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-[11px] font-mono ${dueTone(lvl, d)}`} data-testid={`payments-due-badge-${r.freelance_invoice_id}`}>
                        {lvl === 2 ? <AlertTriangle size={10} /> : <Clock size={10} />}
                        {lvl === 2 ? `Alerte n°2 (${ovd}j)` : lvl === 1 ? `Alerte n°1 (${ovd}j)` : stateLabel}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, tone, testid, icon: Icon }) {
  return (
    <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono flex items-center gap-1.5">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className={`text-lg font-bold font-mono mt-1 ${tone || "text-zinc-900"}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{hint}</div>}
    </div>
  );
}


/**
 * Iter-193c — Inline editor for `payment_delay_days` on a freelance invoice.
 * CEO + GESTIONNAIRE only. Saves via PATCH /freelance/admin/invoices/{id}/payment-delay
 * which recomputes `payment_due_date`, resets alert flags and traces the change in
 * `freelance_invoice_audit_log`.
 */
function DelayEditor({ invoiceId, value, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? 30);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(value ?? 30); }, [value]);

  if (!canEdit) {
    return <span data-testid={`payments-due-delay-${invoiceId}`}>{value != null ? `${value} j` : "—"}</span>;
  }
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 hover:bg-zinc-100 px-1.5 py-0.5 rounded transition-colors group"
        data-testid={`payments-due-delay-edit-${invoiceId}`}
        title="Cliquez pour modifier le délai de paiement"
      >
        {value != null ? `${value} j` : "—"}
        <Pencil size={9} className="opacity-0 group-hover:opacity-60" />
      </button>
    );
  }
  const save = async () => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0 || n > 365) {
      toast.error("Délai invalide (0-365 jours)");
      return;
    }
    if (n === value) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/freelance/admin/invoices/${invoiceId}/payment-delay`, { payment_delay_days: n });
      toast.success(`Délai mis à jour : ${r.data.payment_delay_days} j — échéance ${r.data.payment_due_date}`);
      onSaved && onSaved(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1" data-testid={`payments-due-delay-editor-${invoiceId}`}>
      <input
        type="number"
        min={0}
        max={365}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        className="w-14 px-1.5 py-0.5 text-right border border-zinc-300 rounded text-xs font-mono"
        autoFocus
        disabled={saving}
        data-testid={`payments-due-delay-input-${invoiceId}`}
      />
      <span className="text-zinc-500">j</span>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded"
        data-testid={`payments-due-delay-save-${invoiceId}`}
        title="Enregistrer"
      >
        {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        disabled={saving}
        className="p-0.5 text-zinc-500 hover:bg-zinc-100 rounded"
        data-testid={`payments-due-delay-cancel-${invoiceId}`}
        title="Annuler"
      >
        <X size={11} />
      </button>
    </span>
  );
}
